import React, { useContext, useEffect, useState, CSSProperties } from 'react';
import styles from './Results.module.scss';
import Map from '../../components/map/Map';
import SearchBar from '../../components/search-bar/SearchBar';
import Paginator from '../../components/paginator/Paginator';
import FilterBar from '../../components/filter-bar/FilterBar';
import HeaderControls from '../../components/header-controls/HeaderControls';
import axios from 'axios';
import { DirectoryListType } from '../../components/directory-list/DirectoryList';
import { checkValidZip, distanceOptions, evaluateIsDisabled, mapToGenericResults } from '../../lib/util';
import IGenericResult from '../../interfaces/IGenericResult';
import * as _ from 'lodash';
import ProfileCard from '../../components/profile-card/ProfileCard';
import { AppContext } from '../../context/AppContext';
import { IAppContext, defaultAppState } from '../../context/DefaultAppState';

import BeatLoader from 'react-spinners/BeatLoader';
import DirectoryCardArea from '../../components/directory-card-area/DirectoryCardArea';
import SearchMobile from '../../components/search-mobile/SearchMobile';
import ISearch from '../../interfaces/ISearch';

export interface IResultsFilter {
  specialty: string | null;
  acceptanceStatus: string | null;
  sortType: ResultSortType;
}

export enum ResultSortType {
  none,
  namesAsc,
  namesDesc,
  specialtiesAsc,
  specialtiesDesc,
  acceptanceStatusAsc,
  acceptanceStatusDesc
}

const override: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  margin: 'auto',
  width: '100%'
};

const ResultsPage = (props: any) => {
  const [rawResults, setRawResults] = useState<IGenericResult[]>([]);
  const [chunks, setResultChunks] = useState<IGenericResult[][]>([]);
  // const [showEmailField, setShowEmailField] = useState(false);
  const [resultsFilter, setResultsFilter] = useState<IResultsFilter>({ specialty: null, acceptanceStatus: null, sortType: ResultSortType.none });
  const { planCode, directory, specialty, name, location } = props.match.params;

  const urlLocation = props.location.search;
  const [currentPage, setCurrentPage] = useState(0);
  // const [validationMessage, setValidationMessage] = useState('');
  // const [userEmail, setUserEmail] = useState('');
  const [profileDirections, setProfileDirections] = useState();
  const appContext = useContext(AppContext);
  const [isLoading, setIsLoading] = useState(false);
  const [localData, setLocalData] = useState<any[]>([]);
  const [color] = useState('#1DAA51');

  const hashParams = new URLSearchParams(urlLocation);
  const tempDistance = hashParams.get('distance');
  const searchNonce = hashParams.get('_t');
  

  useEffect(() => {
    initializeResults(planCode, directory, name, specialty, location, tempDistance, setRawResults, appContext);
    appContext.updateChosenSpecialty(specialty);
  }, [planCode, directory, name, specialty, location, tempDistance, searchNonce]);

  useEffect(() => {
    setResultChunks(chunkResults(rawResults));
  }, [rawResults, resultsFilter]);

  const initializeResults = (
    planCode: string | null,
    directory: string | null,
    name: string | null,
    specialty: string | null,
    location: string | null,
    distance: string | null,
    setStateMethod: React.Dispatch<React.SetStateAction<IGenericResult[]>>,
    appContext: IAppContext
  ) => {
    setIsLoading(true);
    let apiRoute = '';
    switch (directory) {
      case DirectoryListType.doctor:
        apiRoute = 'Doctors';
        break;
      case DirectoryListType.facility:
        apiRoute = 'Facilities';
        break;
      case DirectoryListType.pharmacy:
        apiRoute = 'Pharmacies';
        break;
      case DirectoryListType.dentist:
        apiRoute = 'Dentist';
        break;
      case DirectoryListType.phone:
        apiRoute = 'Phone';
        break;
      default:
        apiRoute = 'All';
        break;
    }

    (async () => {
      const distanceParam = distance && parseInt(distance) > 0 ? `?distance=${distance}` : '';
      let reqUrl = apiRoute === 'All' || apiRoute === 'Phone' ? `/${apiRoute}/results/${planCode}/${name}` : `/${apiRoute}/results/${planCode}/${specialty}/${name}/${location}${distanceParam}`;
      let params = '';

      //TODO: Clean up to use switch statement?
      // switch (apiRoute) {
      //   case 'Phone':
      //   case 'All':
      //     reqUrl = `/${apiRoute}/results/${planCode}/${name}`;
      //   break;
      //   default:
      //   break;
      // }

      if (apiRoute === 'Dentist') {
        if (name && name !== 'undefined') {
          params += `&name=${name}`;
        }
        if (specialty && specialty !== 'undefined') {
          params += `&specialty=${specialty}`;
        }
        if (location && location !== 'undefined') {
          params += `&zip=${location}`;
        }
        if (distance && parseInt(distance) > 0) {
          params += `&distance=${distance}`;
        }

        if (params) {
          reqUrl = `/plans/${planCode}/providers?section=dentist${params}`;
        } else {
          reqUrl = `/plans/${planCode}/providers?section=dentist`;
        }
      }

      rebuildContext(planCode, directory, name, specialty, location, distance, appContext);

      const { data } = await axios.get(reqUrl);
      console.log('data HERE', data);
      //
      //const dataOrdered = _.orderBy(data, ['approximateDistance', 'lastName', 'name', 'pharmacyNameDir']);
      const dataOrdered = _.orderBy(data, [
        function (item) {
          return Number(item.approximateDistance);
        },
        function (item) {
          return item.lastName;
        },
        function (item) {
          return item.name;
        },
        function (item) {
          return item.pharmacyNameDir;
        }
      ]);
      console.log('Data ordered', dataOrdered);
      const results = mapToGenericResults(dataOrdered);
      setLocalData(results);

      //rebuildContext(planCode, directory, name, specialty, location, distance, appContext);
      setStateMethod(results);
      setIsLoading(false);
    })();
  };

  const chunkResults = (results: IGenericResult[]): IGenericResult[][] => {
    let filteredResults = results;

    if (resultsFilter.specialty && resultsFilter.specialty.toUpperCase() !== 'ALL SPECIALTIES') {
      filteredResults = filteredResults.filter((result) => result.specialtyGroup.toUpperCase() === resultsFilter.specialty?.toUpperCase());
    }

    if (resultsFilter.acceptanceStatus && resultsFilter.acceptanceStatus !== '') {
      filteredResults = filteredResults.filter((result) => result.acceptingNewPatients?.toUpperCase() === resultsFilter.acceptanceStatus?.toUpperCase());
    }

    switch (parseInt(resultsFilter.sortType.toString())) {
      case ResultSortType.none:
        break;
      case ResultSortType.namesAsc:
        filteredResults = _.orderBy(filteredResults, 'name', 'asc');
        break;
      case ResultSortType.namesDesc:
        filteredResults = _.orderBy(filteredResults, 'name', 'desc');
        break;
      case ResultSortType.specialtiesAsc:
        filteredResults = _.orderBy(filteredResults, 'specialty', 'asc');
        break;
      case ResultSortType.specialtiesDesc:
        filteredResults = _.orderBy(filteredResults, 'specialty', 'desc');
        break;
      case ResultSortType.acceptanceStatusAsc:
        filteredResults = _.orderBy(filteredResults, 'acceptingNewPatients', 'asc');
        break;
      case ResultSortType.acceptanceStatusDesc:
        filteredResults = _.orderBy(filteredResults, 'acceptingNewPatients', 'desc');
        break;
      default:
        break;
    }
    // filteredResults = _.orderBy(filteredResults, ['approximateDistance', 'name']);
    return _.chunk(filteredResults, 4);
  };

  return (
    <div id="results-pdf-container">
      <div className="header-container-mini">
        <HeaderControls />
        <div className="showMobile">
          <SearchMobile />
        </div>
        {/* TODO: CHeck this */}
        {/* <DirectoryButtonArea /> */}
      </div>
      <div className="showDesktop">
        <div className="select-mini-bg">
          <DirectoryCardArea />
          <SearchBar />
          {/* <DirectoryList /> */}
        </div>
      </div>
      {/* <SecondaryControls pdfContainerId="results-pdf-container" showEmailField={showEmailField} setShowEmailField={setShowEmailField} /> */}
      {/* <div>
        {showEmailField ? (
          <>
            <div className={styles.emailFieldContainer}>
              <h4>Email Result</h4>
              <p>Please Enter an Email Address</p>
              <input type="text" placeholder={'example: john.doe@example.com'} onChange={(e) => setUserEmail(e.target.value)} />
              <button
                className={styles.profileButton}
                onClick={() =>
                  validateEmail(userEmail, setValidationMessage, () => {
                    downloadPdf('Directory Results', 100, 'results-pdf-container', true, userEmail);
                    setShowEmailField(!showEmailField);
                  })
                }
              >
                Send Email
              </button>
              <p className={styles.error}>{validationMessage}</p>
            </div>
            <hr />
          </>
        ) : null}
      </div> */}

      <FilterBar resultCount={chunks.reduce((count, chunk) => (count += chunk.length), 0)} resultsFilterMethod={setResultsFilter} resultsFilterValue={resultsFilter} />
      {isLoading ? (
        <BeatLoader color={color} cssOverride={override} size={50} aria-label="Loading Spinner" data-testid="loader" />
      ) : (
        <>
          {!isLoading && !localData.length ? (
            <div className="custom-container">
              <div className="default-card">
                <h4>No providers found. Please expand your search area.</h4>
              </div>
            </div>
          ) : (
            <>
              {chunks.length ? (
                <>
                  <div className={styles.resultsPageContainer}>
                    <div className={styles.profileColumn}>
                      {chunks
                        ? chunks[currentPage]?.map((result, index) => <ProfileCard result={result} key={`pc-${index}`} profileCardIndex={index} handleDirections={setProfileDirections} />)
                        : null}
                    </div>
                    <div className={styles.mapColumn}>
                      <Map currentChunk={chunks[currentPage] || []} profileDirections={profileDirections} />
                    </div>
                  </div>
                  <Paginator resultChunks={chunks} currentPage={currentPage} setCurrentPage={setCurrentPage} />
                </>
              ) : (
                <div className="custom-container">
                  <div className="default-card">
                    <h4>No providers found. Please expand your search area.</h4>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

const rebuildContext = (
  planCode: string | null,
  directory: string | null,
  name: string | null,
  specialty: string | null,
  location: string | null,
  distance: string | null,
  appContext: IAppContext
) => {
  if (planCode) {
    const currentPlan = appContext.plansData.find((p) => p.contractId === planCode);

    if (currentPlan) {
      appContext.updatePlanCode(planCode);
      appContext.updatePlan(currentPlan);
      appContext.updatePlanName(currentPlan.planName);
      appContext.updateDisclaimerUrl(currentPlan.ipdDisclaimerLink);
      appContext.updatePlanState(currentPlan.state);
    }
  }

  if (directory) {
    switch (directory) {
      case DirectoryListType.doctor:
        appContext.updateListType(DirectoryListType.doctor);
        appContext.updateSelected(true);
        break;
      case DirectoryListType.facility:
        appContext.updateListType(DirectoryListType.facility);
        appContext.updateSelected(true);
        break;
      case DirectoryListType.pharmacy:
        appContext.updateListType(DirectoryListType.pharmacy);
        appContext.updateSelected(true);
        break;
      case DirectoryListType.dentist:
        appContext.updateListType(DirectoryListType.dentist);
        appContext.updateSelected(true);
        break;
      case DirectoryListType.phone:
        appContext.updateListType(DirectoryListType.phone);
        appContext.updateSelected(true);
        break;
      case DirectoryListType.all:
        appContext.updateListType(DirectoryListType.all);
        appContext.updateSelected(true);
        break;
      default:
        appContext.updateListType(DirectoryListType.none);
        appContext.updateSelected(false);
        break;
    }
  }

  if (name) {
    appContext.updateName(name);
  }

  if (specialty) {
    appContext.updateSelectedSpecialty(specialty);
  }

  let newSearch = defaultAppState.search;
  if (location) {
    appContext.updateLocation(location);
    const result = checkValidZip(location);
    newSearch = { ...newSearch, isValidZIP: result };
  }

  if (distance) {
    const optionalDistances = distanceOptions();
    const distanceObject: any = optionalDistances.find((distanceObj) => distanceObj.distance.toString() === distance);
    appContext.updateDistance(distanceObject);

    if (distanceObject.distance !== defaultAppState.distance.distance) {
      newSearch = { ...newSearch, required: true };
    }
  }
  const updatedSearch: ISearch = {
    required: newSearch.required,
    isValidZIP: newSearch.isValidZIP
  };

  appContext.updateSearch(updatedSearch);
  let currentLocation = location ? location : null;
  let tempDistance = distance ? parseInt(distance) : defaultAppState.distance.distance;
  const valueResult = evaluateIsDisabled(updatedSearch, currentLocation, tempDistance);
  appContext.updateIsDisabled(valueResult);
};

// const validateEmail = (email: string, setValidationMessage: any, callback: () => void) => {
//   const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
//   let isValid = regex.test(email);

//   if (isValid) {
//     setValidationMessage('');
//     callback();
//   } else {
//     setValidationMessage('Email is not valid. Please check that the email address entered is formatted correctly.');
//   }
// };

// const downloadPdf = (pdfName: string, heightModifier: number, pdfContainerId: string, packageForEmail?: boolean, email?: string) => {
//   const input = document.getElementById(pdfContainerId);
//   html2canvas(input as HTMLElement).then((canvas) => {
//     const imgData = canvas.toDataURL('image/png');
//     const pdf = new jsPDF('p', 'pt', 'a4');
//     const height = 500 + heightModifier;
//     console.log(height);
//     pdf.addImage(imgData, 'PNG', 0, 0, 600, height);
//     // pdf.output('dataurlnewwindow');
//     if (!packageForEmail) {
//       pdf.save(`${pdfName}.pdf`);
//     } else {
//       console.log({
//         planTitle: window.location.host.split('.')[0],
//         planBase: window.location.host === 'localhost:3000' ? 'alignseniorcare.com' : window.location.host,
//         fileAttachment: btoa(pdf.output()),
//         fileName: pdfName,
//         userEmail: email
//       });

//       axios
//         .post(`/SendEmail`, {
//           planTitle: window.location.host.split('.')[0],
//           planBase: window.location.host === 'localhost:3000' ? 'alignseniorcare.com' : window.location.host,
//           fileAttachment: btoa(pdf.output()),
//           fileName: pdfName,
//           userEmail: email
//         })
//         .then((res) => {
//           console.log(res);
//         })
//         .catch((err) => {
//           console.error(err);
//         });
//     }
//   });
// };

export default ResultsPage;
